import React from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1a1a] border border-white/20 rounded-lg p-2 lg:p-3 shadow-xl">
        <p className="text-[10px] lg:text-xs text-zinc-400 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs lg:text-sm font-medium" style={{ color: entry.color }}>
            {entry.name}: <span className="text-white">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Area Chart for Monthly Evolution - Responsive
// data: [{ month: '2026-05', label: 'May', clientes, referidos, validados }] de /api/admin/stats/monthly
export function MonthlyEvolutionChart({ data, className = '' }) {
  const chartData = data || [];
  const hasData = chartData.some(d => d.clientes || d.referidos || d.validados);

  if (!hasData) {
    return (
      <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
        <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
          Evolución Mensual
        </h3>
        <div className="h-[200px] lg:h-[300px] flex items-center justify-center">
          <p className="text-zinc-500 text-sm">Sin datos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
      <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
        Evolución Mensual
      </h3>
      <div className="h-[200px] lg:h-[300px] min-h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorClientes" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ffffff" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#ffffff" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorReferidos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#60a5fa" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorValidados" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#34d399" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis
              dataKey="label"
              stroke="#52525b"
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
            />
            <YAxis
              stroke="#52525b"
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              formatter={(value) => <span className="text-zinc-400 text-[10px] lg:text-xs">{value}</span>}
            />
            <Area
              type="monotone"
              dataKey="clientes"
              name="Clientes nuevos"
              stroke="#ffffff"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorClientes)"
            />
            <Area
              type="monotone"
              dataKey="referidos"
              name="Referidos"
              stroke="#60a5fa"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorReferidos)"
            />
            <Area
              type="monotone"
              dataKey="validados"
              name="Validados"
              stroke="#34d399"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorValidados)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Bar Chart for Conversions by Source - Responsive
export function ConversionBySourceChart({ data, className = '' }) {
  const chartData = data || [
    { source: 'WhatsApp', leads: 85, convertidos: 32 },
    { source: 'Landing', leads: 42, convertidos: 18 },
    { source: 'Manual', leads: 28, convertidos: 12 },
    { source: 'Registro', leads: 56, convertidos: 24 },
  ];

  return (
    <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
      <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
        Conversión por Fuente
      </h3>
      <div className="h-[180px] lg:h-[250px] min-h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" horizontal={false} />
            <XAxis 
              type="number" 
              stroke="#52525b"
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
            />
            <YAxis 
              type="category" 
              dataKey="source" 
              stroke="#52525b"
              tick={{ fill: '#a1a1aa', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="leads" name="Leads" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            <Bar dataKey="convertidos" name="Convertidos" fill="#22c55e" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Pie Chart for Status Distribution - Responsive
export function StatusPieChart({ data, className = '' }) {
  const COLORS = {
    nuevo_lead: '#3b82f6',
    registrado: '#60a5fa',
    contactado: '#eab308',
    prueba_agendada: '#f97316',
    instalado: '#a855f7',
    activo: '#06b6d4',
    validado: '#22c55e',
    recompensa_entregada: '#ffffff'
  };

  const STATUS_LABELS = {
    nuevo_lead: 'Leads',
    registrado: 'Registrados',
    contactado: 'Contactados',
    prueba_agendada: 'Visitas',
    instalado: 'Instalados',
    activo: 'Activos',
    validado: 'Validados',
    recompensa_entregada: 'Completados'
  };

  const chartData = data ? Object.entries(data)
    .filter(([_, value]) => value > 0)
    .map(([status, value]) => ({
      name: STATUS_LABELS[status] || status,
      value,
      color: COLORS[status] || '#71717a'
    })) : [];

  if (chartData.length === 0) {
    return (
      <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
        <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
          Estado de Referidos
        </h3>
        <div className="h-[180px] lg:h-[250px] flex items-center justify-center">
          <p className="text-zinc-500 text-sm">Sin datos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
      <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
        Estado de Referidos
      </h3>
      <div className="h-[180px] lg:h-[250px] min-h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="40%"
              outerRadius="70%"
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              layout="vertical"
              align="right"
              verticalAlign="middle"
              wrapperStyle={{ paddingLeft: '10px', fontSize: '10px' }}
              formatter={(value) => <span className="text-zinc-400 text-[10px] lg:text-xs">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Line Chart for Conversion Rate Trend - Responsive
export function ConversionTrendChart({ data, className = '' }) {
  const chartData = data || [
    { week: 'S1', rate: 28 },
    { week: 'S2', rate: 32 },
    { week: 'S3', rate: 35 },
    { week: 'S4', rate: 31 },
    { week: 'S5', rate: 38 },
    { week: 'S6', rate: 42 },
  ];

  return (
    <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
      <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
        Tendencia de Conversión
      </h3>
      <div className="h-[150px] lg:h-[200px] min-h-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis 
              dataKey="week" 
              stroke="#52525b"
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
            />
            <YAxis 
              stroke="#52525b"
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="rate"
              name="Conversión"
              stroke="#ffffff"
              strokeWidth={2}
              dot={{ fill: '#ffffff', strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5, fill: '#ffffff' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Top Referrers Chart - Responsive
export function TopReferrersChart({ data, className = '' }) {
  const chartData = data?.slice(0, 5).map(r => ({
    name: r.name.split(' ')[0],
    total: r.total_referrals,
    validados: r.validated_referrals
  })) || [];

  if (chartData.length === 0) {
    return (
      <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
        <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
          Top Referidores
        </h3>
        <div className="h-[150px] lg:h-[200px] flex items-center justify-center">
          <p className="text-zinc-500 text-sm">Sin datos disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 lg:p-6 rounded-lg bg-[#0A0A0A] border border-white/10 ${className}`}>
      <h3 className="text-[10px] lg:text-sm uppercase tracking-[0.15em] lg:tracking-[0.2em] text-zinc-500 mb-4 lg:mb-6">
        Top Referidores
      </h3>
      <div className="h-[150px] lg:h-[200px] min-h-[150px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis 
              dataKey="name" 
              stroke="#52525b"
              tick={{ fill: '#a1a1aa', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
            />
            <YAxis 
              stroke="#52525b"
              tick={{ fill: '#71717a', fontSize: 10 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="validados" name="Validados" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default MonthlyEvolutionChart;
